<template>
  <PageWrapper
      :isLoading="isLoading"
      @submitFilters="handleSubmitFilters"
  >
    <HistoryHeader
        title="گزارش عملکرد روزانه"
        button-text="افزودن رکورد جدید"
        primary-button-variant="success"
        @button-click="openCreateModal"
    >

    </HistoryHeader>

    <CustomTable
        :pageNumber="filters.pageNumber"
        :count="filters.count"
        :totalCount="totalRows"
        :tableColumns="tableColumns"
        :tableItems="tableRows"
        @changePage="handleChangePage"
    >
      <template #targetDate="data">
        <span class="date-badge">
          {{ formatDate(data.item.targetDate) }}
        </span>
      </template>

      <template #level="data">
        <div class="level-cell">
          <div class="level-badge" :class="getAvatarMeta(data.item.levelAvatar).colorClass">
            <feather-icon :icon="getAvatarMeta(data.item.levelAvatar).icon" size="16" />
          </div>
          <div class="level-info">
            <div class="level-name">{{ data.item.levelName || "—" }}</div>
            <div class="level-meta">
              <span class="mini-badge">اولویت {{ formatNumber(data.item.levelPriority) }}</span>
              <span class="mini-badge mini-badge--success">
                {{ formatNumber(data.item.levelDiscount) }}%
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #scheduleTotal="data">
        <div class="metric-cell">
          <span class="metric-value">{{ formatNumber(data.item.scheduleTotal) }}</span>
          <span class="metric-unit">دقیقه</span>
        </div>
      </template>

      <template #groupClassTotal="data">
        <div class="metric-cell">
          <span class="metric-value">{{ formatNumber(data.item.groupClassTotal) }}</span>
          <span class="metric-unit">دقیقه</span>
        </div>
      </template>

      <template #termicClassTotal="data">
        <div class="metric-cell">
          <span class="metric-value">{{ formatNumber(data.item.termicClassTotal) }}</span>
          <span class="metric-unit">دقیقه</span>
        </div>
      </template>
      <template #classTotal="data">
        <div class="metric-cell">
          <span class="metric-value"> {{ calculateClassTotal(data.item) }}</span>
          <span class="metric-unit">دقیقه</span>
        </div>
      </template>
      <template #supportTotal="data">
        <div class="metric-cell">
          <span class="metric-value">{{ formatNumber(data.item.supportTotal) }}</span>
          <span class="metric-unit">دقیقه</span>
        </div>
      </template>

      <template #allTime="data">
        <div class="metric-cell">
          <span class="metric-value metric-value--total">{{ formatNumber(data.item.allTime) }}</span>
          <span class="metric-unit">دقیقه</span>
        </div>
      </template>

      <template #description="data">
        <div class="description-cell">
          {{ data.item.description || "—" }}
        </div>
      </template>

      <template #actions="data">
        <div class="actions-cell">
          <b-button
              variant="light"
              class="icon-btn"
              :disabled="isLoading"
              @click="openEditModal(data.item)"
              v-b-tooltip.hover
              title="ویرایش رکورد"
          >
            <feather-icon icon="EditIcon" size="16" />
          </b-button>

          <b-button
              variant="light"
              class="icon-btn icon-btn--danger"
              :disabled="isLoading"
              @click="confirmDelete(data.item)"
              v-b-tooltip.hover
              title="حذف رکورد"
          >
            <feather-icon icon="TrashIcon" size="16" />
          </b-button>
        </div>
      </template>

      <template #table-busy>
        <div class="text-center my-2">در حال دریافت اطلاعات...</div>
      </template>
    </CustomTable>
    <b-modal
        id="delete-history-modal"
        centered
        size="sm"
        hide-header-close
        @hidden="handleDeleteModalClose"
    >
      <template #modal-title>
        <div class="delete-modal-title">تایید حذف</div>
      </template>

      <div v-if="selectedItem" class="delete-modal-content">
        <p>آیا از حذف این رکورد اطمینان دارید؟</p>
        <small class="text-muted">این عمل قابل بازگشت نیست.</small>
      </div>

      <template #modal-footer>
        <div class="delete-modal-footer">
          <b-button
              variant="outline-secondary"
              class="delete-modal-btn"
              @click="$bvModal.hide('delete-history-modal')"
          >
            انصراف
          </b-button>

          <b-button
              variant="danger"
              class="delete-modal-btn delete-modal-btn--danger"
              :disabled="isDeleting"
              @click="deleteRecord"
          >
            <span v-if="isDeleting" class="spinner"></span>
            {{ isDeleting ? "در حال حذف..." : "حذف" }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <CreateHistoryModal
        ref="createHistoryModal"
        :level-loyalty-user-id="LevelLoyaltyUserId"
        :submitting="isSubmitting"
        @submit="handleModalSubmit"
        @cancel="handleModalCancel"
    />
  </PageWrapper>
</template>

<script>
import { BButton, BModal, VBTooltip, BBadge } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import CreateHistoryModal from "@core/components/loyalty/CreateHistoryModal.vue";
import HistoryHeader from "@core/components/loyalty/CreateLevelHeader.vue"
import {
  GetHistoryLevelLoyaltUser,
  DeleteHistoryLevelLoyaltUser
} from "@/libs/Api/LevelLoyalty";
import PageWrapper from "@/views/components/Utils/PageWrapper.vue";
import CustomTable from "@/views/components/Utils/CustomTable.vue";
import CreateLevelHeader from "@core/components/loyalty/CreateLevelHeader.vue";

export default {
  name: "UserLoyaltyHistoryPage",

  components: {
    CreateLevelHeader,
    PageWrapper,
    CustomTable,
    CreateHistoryModal,
    HistoryHeader,
    BButton,
    BModal,
    BBadge,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      isDeleting: false,
      totalRows: 0,
      items: [],
      selectedItem: null,
      userMeta: {
        fullName: "",
        mobile: ""
      },
      filters: {
        pageNumber: 1,
        count: 10,
      },
      tableColumns: [
        { label: "تاریخ هدف", key: "targetDate" },
        { label: "کلاس ساعتی", key: "scheduleTotal" },
        { label: "کلاس گروهی", key: "groupClassTotal" },
        { label: "کلاس ترمیک", key: "termicClassTotal" },
        { label: "پشتیبانی", key: "supportTotal" },
        { label: "مجموع کلاس ها", key: "classTotal" },
        { label: "توضیحات", key: "description" },
        { label: "عملیات", key: "actions", thStyle: { width: "120px" } }
      ],
      avatarOptions: [
        { key: "bronze", label: "برنز", icon: "ShieldIcon", colorClass: "c--bronze" },
        { key: "silver", label: "نقره‌ای", icon: "AwardIcon", colorClass: "c--silver" },
        { key: "gold", label: "طلایی", icon: "StarIcon", colorClass: "c--gold" },
        { key: "diamond", label: "الماس", icon: "HexagonIcon", colorClass: "c--diamond" },
        { key: "platinum", label: "پلاتینیوم", icon: "LayersIcon", colorClass: "c--platinum" },
        { key: "gladiator", label: "گلادیاتور", icon: "ZapIcon", colorClass: "c--gladiator" },
        { key: "elite", label: "نخبه", icon: "TrendingUpIcon", colorClass: "c--elite" }
      ]
    };
  },

  computed: {
    LevelLoyaltyUserId() {
      const params = this.$route?.params || {};
      const raw = params.levelLoyaltUserId || params.id || 0;
      const id = parseInt(String(raw), 10);
      return Number.isFinite(id) && id > 0 ? id : 0;
    },

    tableRows() {
      return this.items.map((item, index) => ({
        rowKey: `${item.id}-${index}`,
        ...item,
      }));
    },
  },

  watch: {
    LevelLoyaltyUserId: {
      immediate: true,
      handler() {
        if (this.LevelLoyaltyUserId > 0) {
          this.fetchList();
        }
      }
    },
    'filters.pageNumber': function() {
      this.fetchList();
    },
  },

  methods: {
    handleSubmitFilters() {
      this.filters.pageNumber = 1;
      this.fetchList();
    },
    calculateClassTotal(item) {
      const scheduleTotal = Number(item.scheduleTotal || 0);
      const groupClassTotal = Number(item.groupClassTotal || 0);
      const termicClassTotal = Number(item.termicClassTotal || 0);

      const total = scheduleTotal + groupClassTotal + termicClassTotal;
      return this.formatNumber(total);
    },
    handleChangePage(pageNumber) {
      if (pageNumber === this.filters.pageNumber) return;
      this.filters.pageNumber = pageNumber;
      this.fetchList();
    },

    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        position: "bottom-center",
        props: {
          title,
          text,
          icon: "",
          variant,
        },
      });
    },

    formatNumber(value) {
      const num = Number(value || 0);
      try {
        return new Intl.NumberFormat("fa-IR").format(num);
      } catch {
        return String(num);
      }
    },

    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "—";
      try {
        return date.toLocaleDateString("fa-IR", {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return String(value).slice(0, 10);
      }
    },

    getAvatarMeta(key) {
      const keyString = String(key || "");
      const found = this.avatarOptions.find((opt) => String(opt.key) === keyString);
      return found || {
        key: keyString,
        label: keyString || "—",
        icon: "ImageIcon",
        colorClass: "c--neutral",
      };
    },

    openCreateModal() {
      const modal = this.$refs.createHistoryModal;
      if (modal) {
        modal.openForCreate();
        this.$nextTick(() => {
          this.$bvModal.show("create-history-modal");
        });
      }
    },

    openEditModal(item) {
      if (!item || !item.id) {
        this.showToast("danger", "خطا", "اطلاعات رکورد نامعتبر است.");
        return;
      }

      const modal = this.$refs.createHistoryModal;
      if (modal) {
        modal.openForEdit(item);
        this.$nextTick(() => {
          this.$bvModal.show("create-history-modal");
        });
      }
    },

    handleModalCancel() {
      this.$bvModal.hide("create-history-modal");
      const modal = this.$refs.createHistoryModal;
      if (modal) {
        modal.reset();
      }
    },

    handleModalSubmit(payload) {
      if (payload.levelLoyaltyUserHistoryId && payload.levelLoyaltyUserHistoryId > 0) {
        this.updateItem(payload);
      } else {
        this.createItem(payload);
      }
    },

    async createItem(payload) {
      try {
        this.isSubmitting = true;
        const modal = this.$refs.createHistoryModal;

        if (modal) {
          await modal.createRecord(payload);
          this.showToast("success", "موفق", "رکورد جدید با موفقیت ایجاد شد");
          this.$bvModal.hide("create-history-modal");
          modal.reset();
          this.filters.pageNumber = 1;
          await this.fetchList();
        }
      } catch (error) {
        console.error("Error creating record:", error);
        const errorMsg = error?.response?.data?.errors || error?.message || "ایجاد رکورد انجام نشد.";
        this.showToast("danger", "خطا", errorMsg);
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateItem(payload) {
      try {
        this.isSubmitting = true;
        const modal = this.$refs.createHistoryModal;

        if (modal) {
          await modal.updateRecord(payload);
          this.showToast("success", "موفق", "ویرایش با موفقیت انجام شد");
          this.$bvModal.hide("create-history-modal");
          modal.reset();
          await this.fetchList();
        }
      } catch (error) {
        console.error("Error updating record:", error);
        const errorMsg = error?.response?.data?.errors || error?.message || "ویرایش انجام نشد.";
        this.showToast("danger", "خطا", errorMsg);
      } finally {
        this.isSubmitting = false;
      }
    },

    confirmDelete(item) {
      this.selectedItem = item;
      this.$bvModal.show("delete-history-modal");
    },

    handleDeleteModalClose() {
      this.selectedItem = null;
    },

    async deleteRecord() {
      if (!this.selectedItem) return;

      try {
        this.isDeleting = true;

        const request = new DeleteHistoryLevelLoyaltUser(this);
        request.setRequestParamDataObj({
          levelLoyaltyUserHistoryId: Number(this.selectedItem.id)
        });

        await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        this.showToast("success", "موفق", "حذف با موفقیت انجام شد");
        this.items = this.items.filter(x => Number(x.id) !== Number(this.selectedItem.id));
        this.totalRows = Math.max(0, this.totalRows - 1);

        if (this.items.length === 0 && this.filters.pageNumber > 1) {
          this.filters.pageNumber -= 1;
          await this.fetchList();
        }

        this.$bvModal.hide("delete-history-modal");
      } catch (error) {
        console.error("Error deleting record:", error);
        const errorMsg = error?.response?.data?.errors || error?.message || "حذف انجام نشد.";
        this.showToast("danger", "خطا", errorMsg);
      } finally {
        this.isDeleting = false;
        this.handleDeleteModalClose();
      }
    },

    async fetchList() {
      if (this.LevelLoyaltyUserId <= 0) {
        this.items = [];
        this.totalRows = 0;
        this.userMeta = { fullName: "", mobile: "" };
        return;
      }

      try {
        this.isLoading = true;

        const request = new GetHistoryLevelLoyaltUser(this);
        const params = {
          LevelLoyaltyUserId: this.LevelLoyaltyUserId,
          PageNumber: Math.max(1, this.filters.pageNumber),
          Count: this.filters.count,
        };

        request.setRequestParam(params);

        const response = await new Promise((resolve, reject) => {
          request.send(resolve, reject);
        });

        if (!response) {
          this.handleFetchError("دریافت اطلاعات انجام نشد.");
          return;
        }

        if (response.isSuccess === false) {
          this.handleFetchError(response.errors || "دریافت اطلاعات با خطا مواجه شد");
          return;
        }

        this.handleFetchSuccess(response.data);
      } catch (error) {
        console.error("Error fetching list:", error);
        this.handleFetchError("خطا در ارتباط با سرور");
      } finally {
        this.isLoading = false;
      }
    },

    handleFetchSuccess(data) {
      const items = Array.isArray(data?.data) ? data.data : [];

      this.totalRows = Number(data?.total || 0);

      const firstUser = items?.[0]?.user || {};
      this.userMeta = {
        fullName: String(firstUser?.fullName || ""),
        mobile: String(firstUser?.mobile || "")
      };

      this.items = items.map(item => {
        const level = item?.levelLoyalt || {};
        return {
          id: Number(item?.levelLoyaltyUserHistoryId || item?.id || 0),
          levelLoyaltyUserId: Number(item?.levelLoyaltyUserId || 0),
          createDate: String(item?.createDate || ""),
          targetDate: String(item?.targetDate || ""),
          scheduleTotal: Number(item?.scheduleTotal || 0),
          groupClassTotal: Number(item?.groupClassTotal || 0),
          termicClassTotal: Number(item?.termicClassTotal || 0),
          supportTotal: Number(item?.supportTotal || 0),
          allTime: Number(item?.allTime || 0),
          description: String(item?.description || ""),
          levelId: Number(level?.id || 0),
          levelPriority: Number(level?.priority || 0),
          levelName: String(level?.name || ""),
          levelAvatar: String(level?.avatar || ""),
          levelDiscount: Number(level?.discount || 0)
        };
      });
    },

    handleFetchError(errorMessage) {
      this.totalRows = 0;
      this.items = [];
      this.userMeta = { fullName: "", mobile: "" };
      this.showToast("danger", "خطا", errorMessage);
    },
  },
};
</script>

<style scoped>
.page-title-wrapper {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.title-section {
  flex: 1;
}

.user-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.add-btn {
  height: 42px;
  border-radius: 12px;
  font-weight: 900;
  font-size: 13px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #28C76F 0%, #20B15A 100%);
  border: 1px solid #28C76F;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(40, 199, 111, 0.3);
  transition: all 0.2s ease;
  white-space: nowrap;
}

.add-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #20B15A 0%, #1A9C4F 100%);
  border-color: #20B15A;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(40, 199, 111, 0.4);
  color: #ffffff;
}

.add-btn:active {
  transform: translateY(0);
}

.date-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f3f4f6;
  font-weight: 900;
  font-size: 12px;
  color: #111827;
  white-space: nowrap;
}

.level-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.level-badge {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.level-name {
  font-weight: 900;
  font-size: 13px;
  color: #111827;
}

.level-meta {
  margin-top: 4px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.mini-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f3f4f6;
  font-weight: 900;
  font-size: 11px;
  color: #111827;
}

.mini-badge--success {
  background: rgba(22,163,74,.12);
  color: #166534;
}

.metric-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-value {
  font-weight: 900;
  font-size: 13px;
  color: #111827;
}

.metric-value--total {
  color: #1e40af;
}

.metric-unit {
  font-size: 12px;
  color: #6b7280;
}

.description-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #6b7280;
}

.actions-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #e5e7eb;
  background: #fff;
  transition: all 0.2s ease;
}

.icon-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.icon-btn--danger {
  color: #b91c1c;
  background: rgba(185,28,28,.06) !important;
  border-color: rgba(185,28,28,.18);
}

.icon-btn--danger:hover:not(:disabled) {
  background: rgba(185,28,28,.12) !important;
  border-color: rgba(185,28,28,.28);
}

.delete-modal-title {
  font-size: 16px;
  font-weight: 900;
  color: #111827;
}

.delete-modal-content {
  text-align: center;
  padding: 10px 0;
}

.delete-modal-content p {
  margin-bottom: 8px;
  font-size: 14px;
  color: #374151;
}

.delete-modal-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.delete-modal-btn {
  height: 42px;
  border-radius: 12px;
  font-weight: 900;
  font-size: 13px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.delete-modal-btn--danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
  border: none;
}

.delete-modal-btn--danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  transform: translateY(-1px);
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,.55);
  border-top-color: rgba(255,255,255,1);
  border-radius: 999px;
  display: inline-block;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.c--neutral { background: #f3f4f6; }
.c--bronze { background: linear-gradient(135deg, rgba(180,83,9,.20), rgba(245,158,11,.22)); }
.c--silver { background: linear-gradient(135deg, rgba(107,114,128,.18), rgba(148,163,184,.22)); }
.c--gold { background: linear-gradient(135deg, rgba(245,158,11,.22), rgba(251,191,36,.24)); }
.c--diamond { background: linear-gradient(135deg, rgba(59,130,246,.18), rgba(99,102,241,.22)); }
.c--platinum { background: linear-gradient(135deg, rgba(20,184,166,.18), rgba(94,234,212,.22)); }
.c--gladiator { background: linear-gradient(135deg, rgba(239,68,68,.14), rgba(244,63,94,.16)); }
.c--elite { background: linear-gradient(135deg, rgba(34,197,94,.16), rgba(16,185,129,.18)); }
</style>